import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import { strings } from '../../resources/strings';
import { MarketplaceTypeEnum } from '../../enums/MarketplaceTypeEnum';
import { useAuth } from '../../contexts/AuthContext';
import ServicesContext from '../../contexts/ServicesContext';
import { SaveOrderImportInput } from '../../services/Marketplaces/SaveOrderImport/SaveOrderImportInput';
import { Button } from '../../common-components/Button/Button';
import { Field, FieldType } from '../../common-components/Field/Field';

interface ModalAmaroNewOrderImportProps {
  close: () => void;
}

const ModalAlert = () => (
  <Alert variant='warning' style={{textAlign:'justify'}}>
    <strong>Atenção!</strong> O processo de importação pode demorar demasiadamente.
    Assim que terminar, o(s) e-mail(s) informado(s) acima serão notificados.
  </Alert>
);

const ModalAmaroNewOrderImport: React.FC<ModalAmaroNewOrderImportProps> = ({
  close
}) => {

  const { session } = useAuth();

  const [file, setFile] = React.useState<any | null | undefined>(undefined);
  const [notificationEmails, setNotificationEmails] = React.useState<string | null | undefined>(session?.user?.email);
  const [importing, setImporting] = React.useState<boolean>(false);

  const {
    alertService,
    fileRepository,
    saveOrderImportService: orderImportService
  } = React.useContext(ServicesContext);

  const memoBody = React.useMemo(() => {
    return (
      <>
        <Field
          type={FieldType.File}
          label={`${strings.File} ".xslx" ou ".csv"`}
          value={file}
          change={(value) => setFile(value)}
          disabled={importing}
          marginTop='0px'
          marginBottom='10px'
        />
        <Field
          type={FieldType.Text}
          label='E-mail(s) para notificação (separar por vírgula)'
          value={notificationEmails}
          change={(value) => setNotificationEmails(value)}
          marginTop='0px'
          marginBottom='15px'
        />
        <ModalAlert />
      </>
    )
  }, [importing, file, notificationEmails]);

  const memoFooter = React.useMemo(() => {
    return (
      <>
        <Button text={strings.Import} click={() => confirmImport()} disabled={importing} />
      </>
    )
  }, [importing, file, notificationEmails]);

  return (
    <Modal
      show={true}
      onHide={() => {
        if (importing) return;
        closeModal();
      }}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          Nova Importação de Pedidos Amaro
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {memoBody}
      </Modal.Body>

      <Modal.Footer style={{ justifyContent: 'center' }}>
        {memoFooter}
      </Modal.Footer>
    </Modal>
  );

  function closeModal() {
    close();
  }

  async function confirmImport() {
    setImporting(true);

    if (!file) {
      setImporting(false);
      alertService.warning(`Informe o arquivo ".xslx" ou ".csv"!`);
      return;
    }

    const name = file.name.replace(/\s/g, '_');

    if (!name.endsWith('.xlsx') && !name.endsWith('.csv')) {
      setImporting(false);
      alertService.warning(`O arquivo deve estar no formato ".xslx" ou ".csv"!`);
      return;
    }

    const timestamp = Date.now();
    const fileName = `amaro_order_import_${timestamp}_file__${name}`;

    const formData = new FormData();
    formData.append(fileName, file, fileName);

    let pathToFile = '';
    try {
      const fileRepositoryResult = await fileRepository.handleOrderImport(formData);
      pathToFile = fileRepositoryResult?.value?.value || '';
    } catch (error) {
      setImporting(false);
      alertService.warning(`Ops! Não estou conseguindo salvar o arquivo. Tente novamente e se o problema persistir avise o time Tech.`);
      return;
    }

    if (!pathToFile) {
      return;
    }

    const input = new SaveOrderImportInput();
    input.pathToFile = pathToFile;
    input.notificationEmails = notificationEmails;
    input.marketplaceType = MarketplaceTypeEnum.Amaro;
    input.userId = session?.user?.id;
    orderImportService.handle(input)
      .then(result => {
        if (result?.value?.result) {
          alertService.info('Os dados de importação foram salvos com sucesso. Agora é só aguardar o processamento, que pode demorar um pouco. Qualquer problema avise o time Tech.');
          closeModal();
          return;
        }
        alertService.warning(`Ops! Parece que houve algo errado ao salvar os dados de importação. Avise o time Tech.`);
      })
      .catch(() => {
        alertService.warning(`Ops! Não estou conseguindo salvar os dados de importação. Tente novamente e se o problema persistir avise o time Tech.`);
      })
      .finally(() => {
        setImporting(false);
      });
  }

}

export { ModalAmaroNewOrderImport };